import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { getBrandingForEmployee } from '../config/brandingStore';
import { formatDate } from '../utils/dateUtils';

Font.register({
  family: 'Amiri',
  src: require("../assets/Amiri.ttf")
});

const CertificatTravailPDF = ({ data }) => {
  // Vérifications de sécurité
  if (!data || !data.employee) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.errorText}>Erreur: Données de l'employé manquantes</Text>
        </Page>
      </Document>
    );
  }
  
  // Helper function pour gérer le genre
  const getGenderInfo = (employee) => {
    const genre = employee.genre?.toLowerCase();
    const isFemale = genre === 'femme';
    
    return {
      title: isFemale ? 'Madame' : 'Monsieur',
      titleLower: isFemale ? 'madame' : 'monsieur',
      ending: isFemale ? 'e' : ''
    };
  };
  
  // Formatter le numéro de référence (gère les anciens et nouveaux formats)
  const formatReference = (ref) => {
    if (!ref) return '0001DG/2MPI/2026';
    
    // Si la référence est déjà au nouveau format (4 chiffres + DG/2MPI + année)
    if (ref.match(/^\d{4}DG\/2MPI\/\d{4}$/)) {
      return ref;
    }
    
    // Si c'est une ancienne référence (ATT-, CERT-, DEC-), la retourner telle quelle
    if (ref.match(/^(ATT|CERT|DEC)-\d+$/)) {
      return ref;
    }
    
    // Sinon, retourner la référence par défaut
    return ref;
  };

  const BRAND = getBrandingForEmployee(data.employee);
  const genderInfo = getGenderInfo(data.employee);
  
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <View style={styles.logo}>
            <Image src={BRAND.pdfLogo} style={styles.logoImage} />
          </View>

          <View style={styles.headerRow}>
            <Text style={styles.headerReference}>
              Réf : {formatReference(data.reference)}
            </Text>
            <Text style={styles.headerDate}>
              {BRAND.pdfHeader.city}, le : {formatDate(data.created_at)}
            </Text>
          </View>

          <Text style={styles.mainTitle}>
            CERTIFICAT DE TRAVAIL
          </Text>

          <View style={styles.formGroup}>
            <Text style={styles.fieldArticle}>
              Je soussigné, Monsieur MOUSSAOUI Bilel, agissant en ma qualité de Président du Conseil d'Administration de la société {BRAND.companyName} SPA, sise au BP89 S -lot N°39, Cité Aidja étage N°02 Zone Industrielle de Boufarik Nord - 09400 Blida, déclare et certifie par la présente que :
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 30 }]}>
              {genderInfo.title} : <Text style={styles.fieldValue}>{data.employee.first_name || '[NOM]'} {data.employee.last_name || '[PRENOM]'}</Text>
            </Text>

            <Text style={styles.fieldArticle}>
              Né le : <Text style={styles.fieldValue}>{formatDate(data.employee.date_naissance) || '………………..'}</Text>
            </Text>

            <Text style={styles.fieldArticle}>
              Demeurant au : <Text style={styles.fieldValue}>{data.employee.adresse || '……………………'}</Text>
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 20 }]}>
              A occupé le poste <Text style={styles.fieldValue}>{data.employee?.poste || '...............'}</Text> au sein de la société {BRAND.companyName}, pour la période allant du <Text style={styles.fieldValue}>{formatDate(data.employee.date_embauche) || '..............'}</Text> au <Text style={styles.fieldValue}>{data.employee.date_sortie ? formatDate(data.employee.date_sortie) : '...................'}</Text>
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 20 }]}>
              {genderInfo.title}, nous quitte libre de tout engagement.
            </Text>

            <Text style={[styles.fieldArticle, { marginTop: 20 }]}>
              En foi de quoi, c{genderInfo.titleLower === 'madame' ? 'ette' : 'e'} présent certificat lui est délivré, pour servir et valoir ce que de droit.
            </Text>
          </View>

          <View style={styles.signature}>
            <View style={styles.signatureRow}>
              <Text style={styles.signatureCompany}> 2MP INDUSTRY SPA </Text>
              <Text style={styles.signatureDepartment}>LA DIRECTION GENERALE</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 12,
    padding: 40,
    paddingTop: 60,
    backgroundColor: '#FFFFFF'
  },
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  logo: {
    width: '100%',
    marginBottom: 10,
  },
  logoImage: {
    width: '100%',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  headerReference: {
    fontSize: 11,
    textAlign: 'left',
  },
  headerDate: {
    fontSize: 11,
    textAlign: 'right',
  },
  mainTitleArabic: {
    fontFamily: 'Amiri',
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 5,
    color: '#1a237e'
  },
  mainTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 30,
    color: '#1a237e'
  },
  formGroup: {
    flex: 1,
    marginBottom: 20
  },
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    borderBottom: '1pt solid #eee',
    paddingBottom: 8
  },
  field: {
    flex: 1,
    fontSize: 12,
    fontWeight: 'bold'
  },
  fieldValue: {
    fontWeight: 'normal',
    color: '#333'
  },
  arabicLabel: {
    fontFamily: 'Amiri',
    fontSize: 14,
    color: '#666',
    marginLeft: 20
  },
  fieldArticle: {
    fontSize: 12,
    lineHeight: 1.5,
    marginBottom: 15,
    textAlign: 'justify'
  },
  signature: {
    marginTop: 60,
    alignItems: 'center'
  },
  signatureRow: {
    alignItems: 'center',
    textAlign: 'center'
  },
  signatureCompany: {
    fontSize: 12,
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 5
  },
  signatureDepartment: {
    fontSize: 12,
    textAlign: 'center'
  },
  errorText: {
    fontSize: 16,
    color: 'red',
    textAlign: 'center',
    marginTop: 100
  }
});

export default CertificatTravailPDF;
